import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { StatusBadge, Progress, Avatar, Card, PageLoad, ErrorMsg, SuccessMsg, SelectDrop } from '../components/ui';
import ImportStudentsModal from '../components/ImportStudentsModal';

export default function StudentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [filters, setFilters] = useState({ status: '', program_id: '', campus: '', assigned_pc_id: '', urgent: '' });
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [feedback, setFeedback] = useState('');
  const [tabCounts, setTabCounts] = useState({ active: 0, archived: 0 });
  const [hostCities, setHostCities] = useState([]);

  const fetchStudents = async (tab = activeTab) => {
    const params = { archived: tab === 'archived' ? 'true' : 'false' };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const res = await api.get('/students', { params });
    setStudents(res.data);
    setTabCounts(prev => ({ ...prev, [tab]: res.data.length }));
  };

  useEffect(() => {
    Promise.all([
      api.get('/students', { params: { archived: 'false' } }),
      api.get('/students', { params: { archived: 'true' } }),
      api.get('/programs'),
      user.role === 'admin' ? api.get('/users') : Promise.resolve({ data: [] }),
      api.get('/hosts/cities'),
    ])
      .then(([sRes, archRes, pRes, uRes, citiesRes]) => {
        setStudents(sRes.data);
        setTabCounts({ active: sRes.data.length, archived: archRes.data.length });
        setPrograms(pRes.data);
        setUsers(uRes.data);
        setHostCities(citiesRes.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user.role]);

  useEffect(() => {
    if (!loading) fetchStudents(activeTab);
  }, [filters, activeTab]);

  const handleUnarchive = async (studentId) => {
    try {
      await api.patch(`/students/${studentId}/unarchive`);
      setFeedback('Student unarchived successfully.');
      setTimeout(() => setFeedback(''), 4000);
      fetchStudents(activeTab);
    } catch {
      setFeedback('Failed to unarchive student.');
      setTimeout(() => setFeedback(''), 4000);
    }
  };

  const studentCities = [...new Set(students.map(s => s.city || s.campus).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));
  const filteredStudents = search.trim()
    ? students.filter(s => `${s.first_name} ${s.last_name} ${s.email}`.toLowerCase().includes(search.toLowerCase()))
    : students;

  if (loading) return <PageLoad />;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Students</h1>
          <div className="page-sub">Manage active practicum students and archived alumni</div>
        </div>
        {user.role === 'admin' && (
          <div className="page-actions">
            <button className="btn btn-secondary" onClick={() => setShowImport(true)}>
              <UploadIcon /> Import CSV
            </button>
            <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); }}>
              + Add Student
            </button>
          </div>
        )}
      </div>

      {feedback && <div style={{ marginBottom: 12 }}>{feedback.startsWith('Failed') ? <ErrorMsg>{feedback}</ErrorMsg> : <SuccessMsg>{feedback}</SuccessMsg>}</div>}

      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
        <div className="tabs">
          <button className={`tab${activeTab === 'active' ? ' active' : ''}`} onClick={() => { setActiveTab('active'); setSearch(''); }}>
            Active <span className="count">{tabCounts.active}</span>
          </button>
          <button className={`tab${activeTab === 'archived' ? ' active' : ''}`} onClick={() => { setActiveTab('archived'); setSearch(''); setFilters(prev => ({ ...prev, urgent: '' })); }}>
            Archived <span className="count">{tabCounts.archived}</span>
          </button>
        </div>
      </div>

      <div className="filters">
        {activeTab === 'active' && (
          <button
            className={`btn ${filters.urgent ? 'btn-primary' : 'btn-secondary'}`}
            aria-pressed={filters.urgent === 'true'}
            onClick={() => setFilters(prev => ({ ...prev, urgent: prev.urgent ? '' : 'true', status: '' }))}
          >
            Urgent
          </button>
        )}
        <div className="input-with-icon filter-search">
          <SearchIcon className="ic" />
          <input className="input" placeholder="Search by name or email…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <SelectDrop minWidth={160} value={filters.status} onChange={v => setFilters({ ...filters, status: v })}
          options={[
            { value: '', label: 'All statuses' },
            { value: 'pre_req', label: 'Pre-Req' },
            { value: 'ready', label: 'Ready' },
            { value: 'active', label: 'Active' },
            { value: 'completing', label: 'Completing' },
            { value: 'complete', label: 'Complete' },
          ]} />
        {user.role === 'admin' && (
          <SelectDrop minWidth={180} value={filters.program_id} onChange={v => setFilters({ ...filters, program_id: v })}
            options={[{ value: '', label: 'All programs' }, ...programs.map(p => ({ value: String(p.id), label: `${p.code} — ${p.name}` }))]} />
        )}
        <SelectDrop minWidth={160} value={filters.city || ''} onChange={v => setFilters({ ...filters, city: v })}
          options={[{ value: '', label: 'All cities' }, ...studentCities.map(c => ({ value: c, label: c }))]} />
        {user.role === 'admin' && (
          <SelectDrop minWidth={150} value={filters.assigned_pc_id} onChange={v => setFilters({ ...filters, assigned_pc_id: v })}
            options={[{ value: '', label: 'All PCs' }, ...users.filter(u => u.role === 'pc').map(u => ({ value: String(u.id), label: u.full_name }))]} />
        )}
      </div>

      {showImport && (
        <ImportStudentsModal
          onClose={() => setShowImport(false)}
          onSuccess={() => {
            fetchStudents('active');
            setTabCounts(prev => ({ ...prev, active: prev.active }));
          }}
        />
      )}

      {showForm && (
        <StudentForm
          programs={programs}
          users={users}
          onSave={() => { setShowForm(false); fetchStudents(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      <Card
        title={`${filteredStudents.length} student${filteredStudents.length === 1 ? '' : 's'}`}
        sub={filters.urgent ? 'Awaiting placement · earliest practicum start first (including overdue dates). Students without a start date are excluded.' : activeTab === 'active' ? 'Currently enrolled or in placement' : 'Practicum completed'}
        padded={false}
      >
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Name</th><th>Program</th><th>City</th>
                <th>Status</th><th style={{ width: 200 }}>Hours</th>
                {filters.urgent && <th>Practicum needed</th>}
                <th>Host</th><th>PC</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map(s => (
                <tr key={s.id} className="row-link" onClick={() => navigate(`/students/${s.id}`)}>
                  <td>
                    <div className="row gap-8">
                      <Avatar name={`${s.first_name} ${s.last_name}`} size="sm" />
                      <div>
                        <div className="nm">{s.first_name} {s.last_name}</div>
                        <div className="sub">{s.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="muted">{s.program_name || s.program_code}</td>
                  <td className="muted">{s.city || s.campus || '—'}</td>
                  <td><StatusBadge status={s.status} /></td>
                  <td><Progress value={parseFloat(s.hours_logged)} max={s.hours_required}
                    variant={s.status === 'complete' ? 'green' : ''} /></td>
                  {filters.urgent && (
                    <td>{s.practicum_start_date ? new Date(`${s.practicum_start_date.slice(0, 10)}T12:00:00`).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}</td>
                  )}
                  <td className="muted">{s.host_name || '—'}</td>
                  <td className="muted">{s.pc_name || '—'}</td>
                  <td>
                    {activeTab === 'archived' && user.role === 'admin' ? (
                      <button
                        className="btn btn-ghost btn-xs"
                        onClick={e => { e.stopPropagation(); handleUnarchive(s.id); }}
                      >
                        Unarchive
                      </button>
                    ) : (
                      <span className="row-action">View →</span>
                    )}
                  </td>
                </tr>
              ))}
              {filteredStudents.length === 0 && (
                <tr>
                  <td colSpan={filters.urgent ? 9 : 8} style={{ textAlign: 'center', padding: 40, color: 'var(--ink-3)' }}>
                    {search ? `No students match "${search}"` : filters.urgent ? 'No students awaiting placement with a practicum start date match these filters' : activeTab === 'archived' ? 'No archived students' : 'No students found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function StudentForm({ programs, users, onSave, onCancel }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    program_id: '', city: '', province: '', cohort_start_date: '', practicum_start_date: '',
    assigned_pc_id: '', hours_required: '', campus_login_id: '',
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const program = programs.find(p => p.id === parseInt(form.program_id));
      await api.post('/students', {
        ...form,
        program_id: parseInt(form.program_id) || null,
        assigned_pc_id: parseInt(form.assigned_pc_id) || null,
        hours_required: parseInt(form.hours_required) || program?.hours_required || 0,
      });
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create student');
    }
  };

  return (
    <div className="card card-pad" style={{ marginBottom: 20 }}>
      <div style={{ fontWeight: 600, marginBottom: 16 }}>Add New Student</div>
      {error && <ErrorMsg>{error}</ErrorMsg>}
      <form onSubmit={handleSubmit} className="form-grid-3" style={{ gap: 12 }}>
        <FormField label="First Name" value={form.first_name} onChange={v => setForm({ ...form, first_name: v })} required />
        <FormField label="Last Name" value={form.last_name} onChange={v => setForm({ ...form, last_name: v })} required />
        <FormField label="Email" type="email" value={form.email} onChange={v => setForm({ ...form, email: v })} required />
        <FormField label="Phone" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
        <FormField label="Campus Login ID" value={form.campus_login_id} onChange={v => setForm({ ...form, campus_login_id: v })} />
        <div>
          <label className="form-label" style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Program</label>
          <select className="select" value={form.program_id} onChange={e => {
            const p = programs.find(pr => pr.id === parseInt(e.target.value));
            setForm({ ...form, program_id: e.target.value, hours_required: p?.hours_required?.toString() || form.hours_required });
          }}>
            <option value="">Select Program</option>
            {programs.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
          </select>
        </div>
        <FormField label="City" value={form.city} onChange={v => setForm({ ...form, city: v })} placeholder="Calgary, Edmonton…" />
        <div>
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Province</label>
          <select className="select" value={form.province} onChange={e => setForm({ ...form, province: e.target.value })}>
            <option value="">— Select province —</option>
            {['Alberta','British Columbia','Manitoba','New Brunswick','Newfoundland and Labrador','Nova Scotia','Northwest Territories','Nunavut','Ontario','Prince Edward Island','Quebec','Saskatchewan','Yukon'].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <FormField label="Hours Required" type="number" value={form.hours_required} onChange={v => setForm({ ...form, hours_required: v })} required />
        <FormField label="Cohort Start" type="date" value={form.cohort_start_date} onChange={v => setForm({ ...form, cohort_start_date: v })} />
        <FormField label="Practicum Start" type="date" value={form.practicum_start_date} onChange={v => setForm({ ...form, practicum_start_date: v })} />
        <div>
          <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Assigned PC</label>
          <select className="select" value={form.assigned_pc_id} onChange={e => setForm({ ...form, assigned_pc_id: e.target.value })}>
            <option value="">Select PC</option>
            {users.filter(u => u.role === 'pc' || u.role === 'admin').map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
        </div>
        <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary">Create Student</button>
        </div>
      </form>
    </div>
  );
}

function FormField({ label, value, onChange, type = 'text', required = false, placeholder = '' }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="input"
        required={required}
        placeholder={placeholder}
      />
    </div>
  );
}

function UploadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-3)', pointerEvents: 'none' }}>
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
}
